<template>
  <!--
    OfficeLocations — the Contact page "Our offices" section.

    Renders one card per office (title, address, optional phone/email, a
    "Get directions" link) with the address beside its map. Full width, below
    the enquiry form. On desktop the address and map sit side by side; on
    mobile they stack (map first, then address). The map is optional per
    office via `office.showMap`.

    Maps use OpenStreetMap's token-free embed iframe centred on each office's
    coordinates, so no map access token needs configuring. Iframes are lazily
    loaded and carry a descriptive title for accessibility.
  -->
  <section class="offices" aria-labelledby="offices-heading">
    <v-container>
      <ScrollReveal>
        <h2 id="offices-heading" class="offices__heading">{{ heading }}</h2>
        <p v-if="subheading" class="offices__subheading">{{ subheading }}</p>
      </ScrollReveal>

      <div class="offices__list">
        <ScrollReveal
          v-for="office in offices"
          :key="office.id || office.title"
        >
          <article class="office-card">
            <div
              class="office-card__grid"
              :class="{ 'office-card__grid--no-map': !officeHasMap(office) }"
            >
              <!-- Address / contact column -->
              <div class="office-card__info">
                <h3 class="office-card__title">{{ office.title }}</h3>

                <address class="office-card__address">
                  <span
                    v-for="(line, index) in office.addressLines"
                    :key="index"
                  >{{ line }}</span>
                </address>

                <div v-if="office.phone" class="office-card__row">
                  <span class="office-card__label">Phone</span>
                  <a
                    :href="`tel:${telHref(office.phone)}`"
                    class="office-card__link"
                  >{{ office.phone }}</a>
                </div>

                <div v-if="office.email" class="office-card__row">
                  <span class="office-card__label">Email</span>
                  <a
                    :href="`mailto:${office.email}`"
                    class="office-card__link"
                  >{{ office.email }}</a>
                </div>

                <a
                  v-if="office.coordinates"
                  :href="directionsUrl(office)"
                  class="office-card__directions"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <v-icon size="small" class="mr-1">mdi-directions</v-icon>
                  Get directions
                </a>
              </div>

              <!-- Map column (optional) -->
              <div v-if="officeHasMap(office)" class="office-card__map">
                <iframe
                  :title="`Map of the ${office.title}`"
                  :src="mapEmbedUrl(office)"
                  class="office-card__iframe"
                  loading="lazy"
                  referrerpolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </article>
        </ScrollReveal>
      </div>
    </v-container>
  </section>
</template>

<script>
// OfficeLocations — presentational "Our offices" section for the Contact page.
//
// Pure presentational component: it takes the offices array plus heading copy
// and renders cards with an optional embedded map beside the address. Map/
// direction URLs are derived from each office's coordinates (no access token
// required).
//
// Uses the Options API to match the project's `plugin:vue/essential` preset.
import ScrollReveal from '@/components/ScrollReveal.vue'

// Half-width (in degrees) of the bounding box used for the embedded map view.
// ~0.008° ≈ a few city blocks, a sensible default zoom for an office pin.
const MAP_BBOX_DELTA = 0.008

export default {
  name: 'OfficeLocations',
  components: {
    ScrollReveal,
  },
  props: {
    // The offices to render (see content/contact.js `offices`).
    offices: {
      type: Array,
      default: () => [],
    },
    // Section heading and optional lead line.
    heading: {
      type: String,
      default: 'Our offices',
    },
    subheading: {
      type: String,
      default: '',
    },
  },
  methods: {
    // An office shows a map only when explicitly enabled and it has coordinates.
    officeHasMap (office) {
      return Boolean(office && office.showMap && office.coordinates)
    },
    // Strip spaces so the `tel:` link dials correctly.
    telHref (phone) {
      return (phone || '').replace(/\s+/g, '')
    },
    // Token-free OpenStreetMap embed centred on the office, with a marker.
    mapEmbedUrl (office) {
      const { lng, lat } = office.coordinates
      const minLng = lng - MAP_BBOX_DELTA
      const minLat = lat - MAP_BBOX_DELTA
      const maxLng = lng + MAP_BBOX_DELTA
      const maxLat = lat + MAP_BBOX_DELTA
      const bbox = `${minLng},${minLat},${maxLng},${maxLat}`
      return (
        'https://www.openstreetmap.org/export/embed.html' +
        `?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`
      )
    },
    // Open turn-by-turn directions to the office in the user's map app.
    directionsUrl (office) {
      const { lng, lat } = office.coordinates
      return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
    },
  },
}
</script>

<style lang="scss" scoped>
.offices {
  padding: 24px 0 72px;
}

.offices__heading {
  font-size: 2rem;
  font-weight: 800;
  color: #41281b;
  margin-bottom: 8px;
}

.offices__subheading {
  font-size: 1.1rem;
  color: #4a4a4a;
  line-height: 1.6;
  margin-bottom: 32px;
  max-width: 640px;
}

.offices__list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.office-card {
  background-color: #fff;
  border: 1px solid rgba(65, 40, 27, 0.1);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(65, 40, 27, 0.06);
}

.office-card__grid {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  align-items: stretch;
}

// When an office has no map, let the address use the full card width.
.office-card__grid--no-map {
  grid-template-columns: 1fr;
}

.office-card__info {
  padding: 32px;
  display: flex;
  flex-direction: column;
}

.office-card__title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #41281b;
  margin-bottom: 16px;
}

.office-card__address {
  font-style: normal;
  line-height: 1.6;
  color: #4a4a4a;
  font-size: 1.05rem;
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.office-card__row {
  display: flex;
  flex-direction: column;
  margin-bottom: 14px;
}

.office-card__label {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #929292;
  margin-bottom: 2px;
}

.office-card__link {
  font-size: 1.05rem;
  color: #4a4a4a;
  text-decoration: none;
}

.office-card__link:hover {
  color: #e7511e;
}

.office-card__directions {
  display: inline-flex;
  align-items: center;
  margin-top: auto;
  padding-top: 8px;
  font-weight: 700;
  color: #e7511e;
  text-decoration: none;
}

.office-card__directions:hover {
  text-decoration: underline;
}

.office-card__map {
  min-height: 260px;
}

.office-card__iframe {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 260px;
  border: 0;
}

// Stack the address above the map on narrow screens.
@media (max-width: 959px) {
  .office-card__grid {
    grid-template-columns: 1fr;
  }

  .office-card__map {
    order: -1; // show the map first, then the details, on mobile
  }
}
</style>
